import { DeepPartial, FieldErrors, FieldValues, useFormContext, useWatch } from "react-hook-form";

import { PriceInputController } from "#shared/view/components/PriceInputController";
import { Space } from "#shared/view/components/Space";
import { SwitchController } from "#shared/view/components/SwitchController";
import { TextareaController } from "#shared/view/components/TextareaController";
import { Title } from "#shared/view/components/Title";
import { NumberInputController } from "#shared/view/designSystem/inputs/NumberInputController";
import { PasswordInputController } from "#shared/view/designSystem/inputs/PasswordInputController";
import { RecommendedNumberInputController } from "#shared/view/designSystem/inputs/RecommendedNumberInputController";
import { RecommendedPriceInputController } from "#shared/view/designSystem/inputs/RecommendedPriceInputController";

import { NativeSelectInputController } from "../../designSystem/inputs/NativeSelectInputController";
import { TextInputController as TextInput } from "../../designSystem/inputs/TextInputController";
import { CheckInput } from "../CheckInput";
import { getNestedDefaultValue } from "./getNestedDefaultValue";
import { FieldType } from "./types";

type Props<T extends FieldValues> = {
  field: FieldType<T>;
  errors: FieldErrors<T>;
  readOnly?: boolean;
  defaultValues?: DeepPartial<T>;
};

export const FormFieldInput = <T extends FieldValues>({
  field,
  readOnly,
  defaultValues,
  errors,
}: Props<T>) => {
  const { control } = useFormContext<T>();

  const fields = useWatch({ control });

  if (field.type === "title") {
    return (
      <>
        <Space h="sm" />
        <Title order={6}>{field.label}</Title>
      </>
    );
  }
  const defaultValue = getNestedDefaultValue<T>(defaultValues, field);

  if (field.type === "select" && field.data) {
    let computedData = [];

    if (typeof field.data === "function") {
      computedData = field.data(fields);
    } else {
      computedData = field.data;
    }

    return (
      <NativeSelectInputController
        key={field.key}
        data={computedData}
        withEmptyData={field.withEmptyData ?? true}
        label={field.label}
        control={control}
        name={field.key}
        required={field.required}
        defaultValue={defaultValue}
        helper={field.helper}
        disabled={readOnly}
        {...field.inputProps}
      />
    );
  }

  if (field.type === "checkbox") {
    return (
      <CheckInput
        key={field.key}
        control={control}
        name={field.key}
        label={field.label}
        disabled={readOnly}
      />
    );
  }

  if (field.type === "switch") {
    return (
      <SwitchController
        key={field.key}
        control={control}
        name={field.key}
        label={field.label}
        disabled={readOnly}
      />
    );
  }
  if (field.type === "price") {
    return (
      <PriceInputController
        key={field.key}
        control={control}
        unit={field.unit}
        name={field.key}
        label={field.label}
        required={field.required}
        disabled={readOnly}
      />
    );
  }

  if (field.type === "number") {
    return (
      <NumberInputController
        key={field.key}
        control={control}
        unit={field.unit}
        name={field.key}
        label={field.label}
        required={field.required}
        unitFactors={field.unitFactors}
        disabled={readOnly}
        helper={field.helper}
        {...field.inputProps}
      />
    );
  }
  if (field.type === "textarea") {
    return (
      <TextareaController
        key={field.key}
        control={control}
        name={field.key}
        label={field.label}
        required={field.required}
        disabled={readOnly}
        placeholder={field.placeholder}
        {...field.inputProps}
      />
    );
  }

  if (field.type === "password") {
    return (
      <PasswordInputController
        key={field.key}
        control={control}
        name={field.key}
        label={field.label}
        error={JSON.stringify(errors[field.key]?.message)}
        disabled={readOnly}
      />
    );
  }

  if (field.type === "component" && field.Component) {
    return field.Component({ key: field.key });
  }

  if (field.type === "recommended") {
    return (
      <RecommendedNumberInputController
        label={field.label}
        name={field.key}
        control={control}
        unit={field.unit}
        computedValue={field.computedValue}
        companyValue={field.companyValue}
        dependencies={field.dependencies}
        required={field.required}
        unitFactors={field.unitFactors}
        disabled={readOnly}
        {...field.inputProps}
      />
    );
  }

  if (field.type === "recommendedPrice") {
    return (
      <RecommendedPriceInputController
        label={field.label}
        name={field.key}
        control={control}
        unit={field.unit}
        computedValue={field.computedValue}
        companyValue={field.companyValue}
        dependencies={field.dependencies}
        required={field.required}
        disabled={readOnly}
        unitFactors={field.unitFactors}
        {...field.inputProps}
      />
    );
  }

  return (
    <TextInput
      {...field}
      {...field.inputProps}
      label={field.label}
      name={field.key}
      control={control}
      disabled={readOnly}
    />
  );
};
